import React, { useState, useEffect, ChangeEvent } from "react";
import { Form, Button, Card, Alert, Spinner } from "react-bootstrap";
import { doc, getDoc, setDoc } from "firebase/firestore";
import MainContainer from "../common/MainContainer";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { CountryCodes } from "../../model/UserModel";
import { useGlobal } from "../../contexts/GlobalContext";
import { maindb, storage } from "../../firebaseConfig";

interface ProfileData {
    firstName: string;
    lastName: string;
    phoneCode: string;
    phoneNumber: string;
    profileImage: string;
}

const UserProfile = () => {

    const [profileData, setProfileData] = useState<ProfileData>({ firstName: "", lastName: "", phoneCode: "+27", phoneNumber: "", profileImage: "" });
    const [image, setImage] = useState<File | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [saving, setSaving] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const { user } = useGlobal();

    useEffect(() => {

        if (user) {
            const fetchUserData = async () => {
                const userRef = doc(maindb, "users", user.uid);
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                    const userDetails = {
                        firstName: userSnap.data().firstName,
                        lastName: userSnap.data().lastName,
                        phoneCode: userSnap.data().phoneCode ?? "+27",
                        phoneNumber: userSnap.data().phoneNumber,
                        profileImage: userSnap.data().profileImage,
                    };
                    setProfileData(userDetails);
                }
                setLoading(false);
            };
            fetchUserData();
        }
    }, [user]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setProfileData({ ...profileData, [e.target.name]: e.target.value });
    };

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setProfileData({ ...profileData, [e.target.name]: e.target.value });
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setImage(e.target.files[0]);
        }
    };

    const handleSave = async () => {

        if (!profileData.firstName.trim() || !profileData.lastName.trim()) {
            setErrorMessage("First name and last name are required.");
            return;
        }
        else if (profileData.phoneNumber !== "" && !profileData.phoneNumber.match(/^\d{2,3}[\s]?\d{3}[\s]?\d{4}$/)) {
            setErrorMessage("Invalid phone number format");
            return;
        }

        if (user) {
            try {
                setSaving(true);
                let imageUrl = profileData.profileImage;

                // Upload new profile image if selected
                if (image) {
                    const imageRef = ref(storage, `profileImages/${user.uid}`);
                    await uploadBytes(imageRef, image);
                    imageUrl = await getDownloadURL(imageRef);
                }

                // Save updated profile data to Firestore
                await setDoc(doc(maindb, "users", user.uid), { ...profileData, phoneNumber: profileData.phoneNumber ?? '', profileImage: imageUrl ?? '' }, { merge: true });

                // Update state with new image URL
                setProfileData(prev => ({ ...prev, profileImage: imageUrl }));

                setSaving(false);
            }
            catch (err) {
                console.error("Error saving user data", err);
                setErrorMessage("Error saving user data");
            }
            finally {
                setErrorMessage("");
            }
        }
    };

    return (
        <MainContainer>
            <h2 className="mb-4">User Profile</h2>
            {user ? (loading ? (<Spinner animation="border" />) :
                (<>
                    <div className="profile-image-container">
                        <Card.Img variant="top" src={profileData.profileImage || "/images/icons//profile.png"} className="profile-image rounded-circle mx-auto d-block" />
                    </div>
                    {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Profile Image</Form.Label>
                            <Form.Control type="file" onChange={handleImageChange} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control type="text" name="firstName" required value={profileData.firstName} onChange={handleChange} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control type="text" name="lastName" required value={profileData.lastName} onChange={handleChange} />
                        </Form.Group>

                        <Form.Group className="mb-3 ">
                            <Form.Label>Cellphone Number</Form.Label>
                            <Form.Group className="d-flex  gap-2">
                                <Form.Select
                                    style={{ width: "300px" }}
                                    value={profileData.phoneCode}
                                    name="phoneCode"
                                    onChange={handleSelectChange}
                                >
                                    {CountryCodes.map((country, index) => (
                                        <option key={index} value={country.code}>
                                            {country.name} ({country.code})
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control
                                    type="text"
                                    name="phoneNumber"
                                    value={profileData.phoneNumber}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Form.Group>

                        <Button className="mt-3" variant="primary" onClick={handleSave} disabled={saving}>
                            {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
                        </Button>

                    </Form>
                </>)
            ) : (
                <p>User is not loaded. Please ensure authentication happens on the login page.</p>
            )}
        </MainContainer>
    );
};

export default UserProfile;